import { useCallback, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { MicIcon } from './Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import useAutoScroll from '@/hooks/useAutoScroll'
import type { RealtimeToken } from '@soniox/client'
import useSyncScroll from '@/hooks/useSyncScroll'
import TranscriptTokenStream from './TranscriptTokenStream'

/** Centered idle card shown inside a pane before any speech arrives. */
function EmptySpeakingCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-sm rounded-3xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-6 text-center shadow-[var(--shadow-md)] sm:px-8 sm:py-10">
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent-light)] text-[var(--accent)] ring-1 ring-[var(--ring)] sm:mb-5 sm:h-14 sm:w-14"
          aria-hidden
        >
          <MicIcon className="h-6 w-6 sm:h-7 sm:w-7" />
        </div>
        <p className="text-sm font-semibold text-[var(--text-primary)] sm:text-base">{title}</p>
        <p className="mt-2 text-xs leading-relaxed text-[var(--text-secondary)] sm:text-sm">{body}</p>
      </div>
    </div>
  )
}

interface RendererProps {
  sourceTokens: RealtimeToken[]
  targetTokens: RealtimeToken[]
  sourcePaneOpen?: boolean
  sourcePaneRendered?: boolean
  targetPaneOpen?: boolean
  targetPaneRendered?: boolean
  /**
   * Output tints its newest utterance and bumps that text a couple of pixels.
   * False in Meeting mode, where the transcript is reference material rather
   * than the thing being followed (BRAI-416). Defaults true so existing
   * callers keep the interview behaviour.
   */
  highlightNewestOutput?: boolean
  sourcePanePercent?: number
  targetPanePercent?: number
  /** Optional header rendered above the source scroll body in the same column. */
  sourceHeader?: ReactNode
  /** Optional header rendered above the target scroll body in the same column. */
  targetHeader?: ReactNode
}

export default function Renderer({
  sourceTokens,
  targetTokens,
  sourcePaneOpen = true,
  sourcePaneRendered = true,
  targetPaneOpen = true,
  targetPaneRendered = true,
  highlightNewestOutput = true,
  sourcePanePercent = 50,
  targetPanePercent = 50,
  sourceHeader,
  targetHeader,
}: RendererProps) {
  const { t } = useUiStrings()
  const [sourceAway, setSourceAway] = useState(false)
  const [targetAway, setTargetAway] = useState(false)
  const isSourceVisible = sourcePaneOpen && sourcePaneRendered
  const isTargetVisible = targetPaneOpen && targetPaneRendered
  const hasTokens = sourceTokens.length > 0 || targetTokens.length > 0
  // Once tokens exist, keep both scroll columns mounted (CSS-hide when closed)
  // so collapse/expand is layout-only: scrollTop, listeners, and pin/away survive.
  const mountSourceColumn = hasTokens || sourcePaneRendered
  const mountTargetColumn = hasTokens || targetPaneRendered

  const {
    ref: sourceScrollRef,
    jumpToLive: jumpSource,
    isAutoScrollingRef: isSourceAutoScrollingRef,
  } = useAutoScroll(sourceTokens, {
    onAwayFromBottomChange: setSourceAway,
  })
  const {
    ref: targetScrollRef,
    jumpToLive: jumpTarget,
    isAutoScrollingRef: isTargetAutoScrollingRef,
  } = useAutoScroll(targetTokens, {
    onAwayFromBottomChange: setTargetAway,
  })

  useSyncScroll(
    sourceScrollRef,
    targetScrollRef,
    isSourceAutoScrollingRef,
    isTargetAutoScrollingRef
  )

  // Only surface the jump-to-live button for a pane the user can actually see,
  // so a collapsed/off-screen pane's away-state never keeps the button up.
  const showJumpToLive =
    hasTokens && ((isSourceVisible && sourceAway) || (isTargetVisible && targetAway))

  /**
   * Re-pin both panes to the live edge and let each hook re-enable auto-follow.
   */
  const scrollToLive = useCallback(() => {
    jumpSource()
    jumpTarget()
  }, [jumpSource, jumpTarget])

  // Each column collapses to `minmax(0, …)` when its pane is hidden so the
  // body width matches the toggled header. When both panes are visible we use
  // a 0px floor for the two-column template so the fr distribution matches
  // the divider position — a 220px minmax floor would grow each pane past its
  // intended pixel width by distributing free space on top of the min,
  // drifting the divider away from the actual boundary once a pane hits the
  // resize clamp. The parent's resize handler prevents each pane from going
  // below MIN_TRANSCRIPT_PANEL_PX in pixels.
  const sourceColumnMin = isSourceVisible ? 220 : 0
  const targetColumnMin = isTargetVisible ? 220 : 0
  const gridTemplate = !mountSourceColumn
    ? mountTargetColumn
      ? `minmax(${targetColumnMin}px, 1fr)`
      : 'minmax(220px, 1fr)'
    : !mountTargetColumn
      ? `minmax(${sourceColumnMin}px, 1fr)`
      : !isSourceVisible
        ? `minmax(0px, 0fr) minmax(${targetColumnMin}px, 1fr)`
        : !isTargetVisible
          ? `minmax(${sourceColumnMin}px, 1fr) minmax(0px, 0fr)`
          : `minmax(0px, ${Math.max(sourcePanePercent, 0.001)}fr) minmax(0px, ${Math.max(
              targetPanePercent,
              0.001
            )}fr)`
  const gridStyle = {
    '--renderer-grid-template': gridTemplate,
  } as CSSProperties

  /**
   * Body for one transcript column: tokens, waiting copy, or idle empty card.
   * Kept inside the same column as that pane's header so narrow layouts stack
   * as header→content→header→content instead of all headers then all bodies.
   */
  const renderPaneBody = (
    tokens: RealtimeToken[],
    keyPrefix: string,
    showTimestamp: boolean,
    waitingLabel: string,
    scrollRef: typeof sourceScrollRef,
    testId: string,
    highlightOnArrival = false
  ) => {
    if (!hasTokens) {
      return (
        <div
          ref={scrollRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
          data-testid={testId}
        >
          <EmptySpeakingCard title={t.startSpeakingTitle} body={t.startSpeakingBody} />
        </div>
      )
    }

    return (
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 [overflow-wrap:anywhere] sm:px-4 sm:py-5"
        data-testid={testId}
      >
        {tokens.length > 0 ? (
          <TranscriptTokenStream
            tokens={tokens}
            keyPrefix={keyPrefix}
            showTimestamp={showTimestamp}
            highlightOnArrival={highlightOnArrival}
          />
        ) : (
          <p className="text-xs text-[var(--text-tertiary)] sm:text-sm">{waitingLabel}</p>
        )}
      </div>
    )
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <section
        className="flex min-h-0 flex-1 flex-col overflow-hidden border-0 bg-[var(--bg-secondary)] shadow-none"
        aria-label={t.transcriptAria}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden motion-safe:animate-transcript-layout">
          <div
            className={[
              'grid min-h-0 flex-1 grid-cols-1 divide-y divide-[var(--border-color)] md:divide-y-0 md:[grid-template-columns:var(--renderer-grid-template)]',
              mountSourceColumn && mountTargetColumn && isSourceVisible && isTargetVisible
                ? 'md:divide-x'
                : '',
            ].join(' ')}
            style={gridStyle}
          >
            {mountSourceColumn && (
              <div
                className={[
                  'flex min-h-0 flex-1 flex-col overflow-hidden transition-opacity duration-300 ease-out',
                  isSourceVisible
                    ? 'opacity-100'
                    : 'pointer-events-none max-md:hidden opacity-0 md:min-w-0',
                ].join(' ')}
                aria-hidden={!isSourceVisible}
                data-testid="renderer-source-column"
              >
                {sourceHeader}
                {renderPaneBody(
                  sourceTokens,
                  'dev-src',
                  true,
                  t.waitingSource,
                  sourceScrollRef,
                  'renderer-source-scroll'
                )}
              </div>
            )}
            {mountTargetColumn && (
              <div
                className={[
                  'flex min-h-0 flex-1 flex-col overflow-hidden transition-opacity duration-300 ease-out',
                  isTargetVisible
                    ? 'opacity-100'
                    : 'pointer-events-none max-md:hidden opacity-0 md:min-w-0',
                ].join(' ')}
                aria-hidden={!isTargetVisible}
                data-testid="renderer-target-column"
              >
                {targetHeader}
                {renderPaneBody(
                  targetTokens,
                  'dev-tgt',
                  // Same as Input: without it this pane marked no time at all, so
                  // there was nothing to read the two columns against.
                  true,
                  t.waitingTranslation,
                  targetScrollRef,
                  'renderer-target-scroll',
                  // Output only. The Input pane shows the same speech a beat
                  // earlier, so tinting both would double the flashing for one
                  // utterance. Off entirely in Meeting mode (BRAI-416).
                  highlightNewestOutput
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <div
        className={[
          'pointer-events-none absolute inset-x-0 bottom-8 z-50 flex justify-center transition-all duration-200 ease-out',
          showJumpToLive ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        ].join(' ')}
        aria-hidden={!showJumpToLive}
        data-testid="jump-to-live"
      >
        <button
          type="button"
          tabIndex={showJumpToLive ? 0 : -1}
          onClick={scrollToLive}
          className="pointer-events-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white opacity-60 shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5 hover:opacity-100"
        >
          {t.jumpToLive}
        </button>
      </div>
    </div>
  )
}
